import React, { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";

import { AnalyzeResourceResponseBodyType } from "@intelligems/ig-types";
import { Grid } from "@mui/material";
import { UseQueryResult } from "@tanstack/react-query";

import {
  useDashboardExperienceOrDefault,
  useExperimentAnalysis,
} from "../../../api/analysis/analysis";
import { useConfiguration } from "../../../api/configurations/use-configuration";
import { useGetExperiencesList } from "../../../api/experiences-list/get-experiences-list";
import {
  activeExperiencesFilter,
  filterDashboardData,
  getGraphConfig,
} from "../../../api/formatters/dashboard";
import { DashboardPageName } from "../../../api/types/dashboards";
import { useIsAdmin } from "../../../api/user-auths/is-admin";
import {
  NoExperimentDataOverlay,
  NoExperimentOverlay,
  NoExperimentSubscriptionDataOverlay,
} from "../../../components/NoDataOverlay/NoExperimentDataOverlay";
import { experienceAnalysis } from "../../../store/slices/ai";
import {
  initEnsuredExperienceFromDto,
  selectedExperienceState,
} from "../../../store/slices/experiences";
import {
  initVariationsState,
  selectedVariations,
} from "../../../store/slices/variations";
import { IAppState } from "../../../store/types/store";
import Loading from "../../../utils/Loading";
import { AiSection } from "../aiSection/AiSection";
import { DashboardGraphs } from "../DashboardGraphs";
import { DashboardHeader } from "../DashboardHeader";
import { DashboardHeaderSimple } from "../DashboardHeaderSimple";
import { OverviewTable } from "../Pages/OverviewDashboard/OverviewTable";
import { ProfitTable } from "../Pages/ProfitDashboard/ProfitTable";

import { CogsWarning } from "./CogsWarning";
import { hasFiltersBeenModified } from "./utils";

interface DashboardPageTemplateProps {
  pageName: DashboardPageName;
  showCogsWarning?: boolean;
}

export const DashboardPageTemplate = ({
  pageName,
  showCogsWarning,
}: DashboardPageTemplateProps) => {
  const experiencesList = useGetExperiencesList({ category: "experiment" });
  const shouldShowProfit = useConfiguration<boolean>(
    "should_show_profit_in_dashboard"
  );
  const isAdmin = useIsAdmin();
  const analysis = useSelector(experienceAnalysis);
  const enableAi = useConfiguration<boolean>("enable_ai")?.value;

  const hasAiAccess = isAdmin || enableAi;

  const dashboardData: UseQueryResult<AnalyzeResourceResponseBodyType>[] =
    useExperimentAnalysis(getGraphConfig(pageName));
  const experience = useDashboardExperienceOrDefault(
    pageName === "overview"
      ? filterDashboardData(dashboardData, "overview")
      : undefined
  );

  const dispatch = useDispatch();
  const experienceState = useSelector(selectedExperienceState);
  const variations = useSelector(selectedVariations);

  useEffect(() => {
    if (experience.isSuccess) {
      dispatch(
        initEnsuredExperienceFromDto({ experience: experience.data.experience })
      );
      dispatch(
        initVariationsState({
          variations: experience.data.experience.variations,
        })
      );
    }
  }, [experience.isSuccess]);

  const dashboardFilters = useSelector(
    (state: IAppState) => state.dashboardFilters
  );

  const shouldNotRenderGraphs = useMemo(() => {
    if (
      dashboardData &&
      dashboardData[0].isSuccess &&
      dashboardData[0].data?.metrics.length < 1 &&
      !hasFiltersBeenModified(dashboardFilters)
    ) {
      if (pageName === "statSig") {
        return !dashboardData[0].data?.metrics.some(
          (metric) => metric.stag_sig_loss
        );
      } else {
        return true;
      }
    }
    return false;
  }, [dashboardData, dashboardFilters, pageName]);

  if (
    !dashboardFilters.experienceId &&
    experienceState.isSuccess &&
    experience.isSuccess
  ) {
    return <NoExperimentOverlay />;
  }
  if (
    experienceState.isSuccess &&
    experienceState.experience &&
    experiencesList.isSuccess &&
    dashboardFilters.experienceId &&
    experience.isSuccess &&
    experience.data.experience
  ) {
    return (
      <Grid container direction={"column"} rowSpacing={3}>
        {dashboardData && shouldShowProfit?.value && showCogsWarning && (
          <CogsWarning
            dashboardProfitData={filterDashboardData(dashboardData, "overview")}
          />
        )}
        {shouldNotRenderGraphs ? (
          <DashboardHeaderSimple
            latestRunTs={dashboardData[0]?.data?.latestRunTs}
            activeExperiences={activeExperiencesFilter(
              experiencesList.data.experiencesList
            )}
          />
        ) : (
          <DashboardHeader
            activeExperiences={activeExperiencesFilter(
              experiencesList.data.experiencesList
            )}
            pageName={pageName}
            latestRunTs={dashboardData[0]?.data?.latestRunTs}
          />
        )}
        {pageName === "overview" && hasAiAccess && !analysis.isHidden ? (
          <AiSection />
        ) : null}
        {shouldNotRenderGraphs && pageName === "subscriptions" ? (
          <NoExperimentSubscriptionDataOverlay />
        ) : shouldNotRenderGraphs ? (
          <NoExperimentDataOverlay />
        ) : pageName === "profit" ? (
          <Grid item container direction={"row"} spacing={2}>
            <Grid item sm={12} md={7}>
              <ProfitTable
                variations={variations}
                profitMetrics={filterDashboardData(dashboardData, "overview")!}
              />
            </Grid>
            <Grid item md={5} xs={12}>
              <DashboardGraphs
                resourceType={"experiences"}
                resource={experienceState.experience}
                config={getGraphConfig(pageName)}
                dashboardData={dashboardData}
                pageName={"profit"}
                metricFilterIndex={
                  dashboardFilters.statSigView === "best" ? 0 : 1 || 0
                }
              />
            </Grid>
          </Grid>
        ) : (
          <Grid item container direction={"column"}>
            <Grid item>
              <DashboardGraphs
                resourceType={"experiences"}
                resource={experienceState.experience}
                config={getGraphConfig(pageName)}
                dashboardData={dashboardData}
                metricFilterIndex={
                  dashboardFilters.statSigView === "best" ? 0 : 1 || 0
                }
              />
            </Grid>
            {pageName === "overview" && !shouldNotRenderGraphs ? (
              <OverviewTable
                data={filterDashboardData(dashboardData, "overview")}
                variations={variations}
              />
            ) : null}
          </Grid>
        )}
      </Grid>
    );
  }
  return <Loading />;
};
